import React, { useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import styles from "./flag.module.css"
import { isColorDark } from "../../utils/utils"

const Flag = ({ name, color, title }) => {
    const isDark = isColorDark(color)
    const label = title || name
    const flagRef = useRef(null)
    const nameRef = useRef(null)
    const tooltipRef = useRef(null)
    const [tooltip, setTooltip] = useState({
        visible: false,
        anchor: null,
        style: {},
        placement: "top",
    })

    useLayoutEffect(() => {
        if (!tooltip.visible || !tooltip.anchor || !tooltipRef.current) {
            return
        }

        const margin = 8
        const gap = 9
        const tooltipRect = tooltipRef.current.getBoundingClientRect()
        const center = tooltip.anchor.left + (tooltip.anchor.width / 2)
        const maxLeft = window.innerWidth - tooltipRect.width - margin
        const left = Math.min(Math.max(center - (tooltipRect.width / 2), margin), Math.max(margin, maxLeft))
        const arrowLeft = Math.min(Math.max(center - left, 12), tooltipRect.width - 12)
        const topPlacement = tooltip.anchor.top - tooltipRect.height - gap
        const placement = topPlacement < margin ? "bottom" : "top"
        const top = placement === "top" ? topPlacement : Math.min(tooltip.anchor.bottom + gap, window.innerHeight - tooltipRect.height - margin)

        setTooltip(previousValue => ({
            ...previousValue,
            placement,
            style: {
                left,
                top: Math.max(margin, top),
                "--tooltip-arrow-left": `${arrowLeft}px`,
            },
        }))
    }, [tooltip.visible, tooltip.anchor])

    const showTooltip = () => {
        if (!label || !flagRef.current) {
            return
        }

        const visibleName = name || ""
        const isSameText = label === visibleName
        const nameIsTruncated = nameRef.current ? nameRef.current.scrollWidth > nameRef.current.clientWidth : false
        if (isSameText && !nameIsTruncated) {
            return
        }

        const rect = flagRef.current.getBoundingClientRect()
        setTooltip({
            visible: true,
            anchor: {
                left: rect.left,
                top: rect.top,
                bottom: rect.bottom,
                width: rect.width,
            },
            style: {
                left: rect.left,
                top: rect.top,
                "--tooltip-arrow-left": "50%",
            },
            placement: "top",
        })
    }

    const hideTooltip = () => {
        setTooltip(previousValue => ({ ...previousValue, visible: false }))
    }

    return (
        <>
            <div
                ref={flagRef}
                className={`${styles.flag} ${styles[isDark ? "light" : "dark"]}`}
                style={{ "--flag-color": color }}
                onMouseEnter={showTooltip}
                onMouseLeave={hideTooltip}
                onFocus={showTooltip}
                onBlur={hideTooltip}
            >
                <i />
                <span ref={nameRef} className={styles.name}>{name}</span>
            </div>
            {tooltip.visible && createPortal(
                <span
                    ref={tooltipRef}
                    className={`${styles.tooltip} ${styles[tooltip.placement]}`}
                    style={tooltip.style}
                >
                    <span className={styles.tooltipLabel}>{label}</span>
                </span>,
                document.body
            )}
        </>
    )
}

export default Flag
